import { FC, MouseEvent, useState } from "react"
import { colors } from "../../utils/theme"
import {
  makeStyles,
  TableHead as MuiTableHead,
  TableCell as MuiTableCell,
  TableRow,
  TableSortLabel,
  TableHeadProps as MuiTableHeadProps,
  Table,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  Typography,
} from "@material-ui/core"
import { InAppResourcesTableData, Order, Resource } from "../../utils/types"
import ChevronDownIcon from "../atoms/ChevronDownIcon"
import { stableSort, getComparator } from "../../utils/helpers"
import TablePaginationActions from "@material-ui/core/TablePagination/TablePaginationActions"
import TechniqueAndInterventionsHelpIcon from "./TechniquesAndInterventionsHelpIcon"
import { useInAppResources } from "../../hooks/useInAppResources"
import InAppResourcesMoreButton from "./InAppResourcesMoreButton"
import { useUser } from "../../hooks/useUser"
import { InAppResourceDialog } from "../organisms/InAppResourceDialog"
import utc from "dayjs/plugin/utc"
import dayjs from "dayjs"
dayjs.extend(utc)

const useStyles = makeStyles((theme) => ({
  table: {
    color: colors.neutral.denim,
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid ${colors.neutral.clay}`,
    backgroundColor: colors.neutral.white,
    marginTop: theme.spacing(3),
  },
  tableHeaderCell: {
    color: colors.neutral.denim,
    fontWeight: 700,
    borderBottom: `1px solid ${colors.neutral.clay}`,
  },
  tableSortLabel: {
    color: `${colors.neutral.denim} !important`,
    fontWeight: 700,
  },
  noData: {
    width: "100%",
    padding: 27,
    textAlign: "center",
    border: `1px solid ${colors.neutral.clay}`,
    borderTop: 0,
    backgroundColor: colors.neutral.white,
  },
  noDataFirstLine: {
    fontWeight: 700,
    marginBottom: 6,
  },
  hyperlink: {
    color: colors.primary.periwinkle,
    textDecoration: "underline",
    cursor: "pointer",
  },
  cell: {
    maxWidth: "15vw",
    fontWeight: 300,
  },
  questionnaireColumn: {
    display: "flex",
    alignItems: "center",
  },
  questionnaireScore: {
    height: 33,
    width: 33,
    borderRadius: 4,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  helpIconContainer: {
    display: "flex",
    alignItems: "center",
  },
  helpIcon: {
    marginLeft: theme.spacing(2),
    height: 24,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}))

interface Column {
  id: keyof InAppResourcesTableData
  label: string
}

type TableHeadProps = {
  order: Order
  orderBy: string
  onRequestSort: (
    event: MouseEvent<unknown>,
    property: keyof InAppResourcesTableData
  ) => void
} & MuiTableHeadProps

const TableHead: FC<TableHeadProps> = ({ order, orderBy, onRequestSort }) => {
  const classes = useStyles()
  const createSortHandler =
    (property: keyof InAppResourcesTableData) =>
    (event: MouseEvent<unknown>) => {
      onRequestSort(event, property)
    }
  const columns: Column[] = [
    {
      id: "title",
      label: "Title",
    },
    {
      id: "header_image",
      label: "Header Image",
    },
    {
      id: "author",
      label: "Source",
    },
    {
      id: "created_on",
      label: "Date",
    },
    {
      id: "content",
      label: "Content",
    },
    {
      id: "more",
      label: "",
    },
  ]
  return (
    <MuiTableHead>
      <TableRow>
        {columns.map((column) => (
          <MuiTableCell
            key={column.id}
            sortDirection={orderBy === column.id ? order : false}
            className={classes.tableHeaderCell}
            align={column.label === "" ? "right" : undefined}
            padding={column.label === "" ? "none" : undefined}
          >
            {column.label !== "" ? (
              <TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : "asc"}
                onClick={createSortHandler(column.id)}
                IconComponent={(props) => <ChevronDownIcon {...props} />}
                classes={{
                  root: classes.tableSortLabel,
                  active: classes.tableSortLabel,
                }}
              >
                {column.label}
              </TableSortLabel>
            ) : (
              <TechniqueAndInterventionsHelpIcon />
            )}
          </MuiTableCell>
        ))}
      </TableRow>
    </MuiTableHead>
  )
}

const InAppResourcesTable: FC = () => {
  const classes = useStyles()
  const user = useUser()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false)
  const [item, setItem] = useState<Resource | undefined>(undefined)
  const [order, setOrder] = useState<Order>("asc")
  const [orderBy, setOrderBy] = useState<keyof InAppResourcesTableData>("title")
  const { inAppResources } = useInAppResources(
    user.user.organizations?.[0].id ?? -1
  )
  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }
  return (
    <>
      <Table className={classes.table}>
        <TableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={(
            event: React.MouseEvent<unknown>,
            property: keyof InAppResourcesTableData
          ) => {
            const isAsc = orderBy === property && order === "asc"
            setOrder(isAsc ? "desc" : "asc")
            setOrderBy(property)
          }}
        />
        {inAppResources && inAppResources.length > 0 && (
          <TableBody>
            {stableSort(
              rowsPerPage > 0
                ? inAppResources.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : inAppResources,
              getComparator(order, orderBy)
            ).map((row, index) => {
              const onClick = () => {
                //navigate
                const item: Resource = {
                  id: row.id.data,
                  content: row.content.data,
                  content_type: row.content_type.data,
                  author: row.author.data,
                  created_by: row.created_by.data,
                  owner_org_id: row.owner_org_id.data,
                  public: row.public.data,
                  header_image: row.header_image.data,
                  created_on: row.created_on.data,
                  updated_on: row.updated_on.data,
                  title: row.title.data,
                }
                setItem(item)
                setOpenScheduleDialog(true)
              }
              return (
                <TableRow key={index}>
                  <TableCell
                    className={classes.hyperlink}
                    role="button"
                    onClick={onClick}
                  >
                    {row.title.data ? row.title.data : "-"}
                  </TableCell>
                  <TableCell className={classes.cell} role="button">
                    {row.header_image.data ? row.header_image.data : "-"}
                  </TableCell>
                  <TableCell
                    className={classes.cell}
                    role="button"
                    onClick={onClick}
                  >
                    {row.author.data ? row.author.data : "-"}
                  </TableCell>
                  <TableCell
                    className={classes.cell}
                    role="button"
                    onClick={onClick}
                  >
                    {dayjs(row.updated_on.data).format("MM/DD/YYYY")}
                  </TableCell>
                  <TableCell
                    className={classes.cell}
                    role="button"
                    onClick={onClick}
                  >
                    <Typography noWrap>{row.content.data}</Typography>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <InAppResourcesMoreButton
                      canOpen={
                        row.created_by.data === user?.user.id ||
                        (!row.created_by.data &&
                          user?.user?.email?.endsWith("@ksanahealth.com"))
                      }
                      id={row.id.data}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        )}
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
              colSpan={12}
              count={inAppResources.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  "aria-label": "rows per page",
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
      <InAppResourceDialog
        open={openScheduleDialog}
        newResource={false}
        item={item}
        onClose={() => {
          setOpenScheduleDialog(false)
        }}
      />
    </>
  )
}

export default InAppResourcesTable
